import React, { useEffect } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import { IndianRupee } from "lucide-react";
const rupeeSymbol = "./rupee-indian.png"
// Register Open Sans font
Font.register({
  family: "Open Sans",
  fonts: [
    { src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf", fontWeight: "normal" },
    { src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf", fontWeight: "bold" }
  ]
});

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Open Sans",
  },
  section: {
    marginBottom: 15,
  },
  title: {
    fontSize: 12, // Decreased font size
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    backgroundColor: "#0E2841",
    color: "white",
    padding: 6,
    borderRadius: 5,
    marginBottom: 10,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: 20,
  },
  tableHeader: {
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    backgroundColor: "#0D215C",
    color: "white",
    fontSize: 8,

    textAlign: "center",
    padding: 4,
    // border: "1px solid white",
  },
  tableCell: {
    fontSize: 8,
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
 
    color: "#0D215C",
    textAlign: "center",
    padding: 4,
    // border: "1px solid white",
  },
  tableRow: {
    flexDirection: "row",
  },
  disclaimer: {
    fontSize: 10,
    fontStyle: "italic",
    marginTop: 10,
    paddingHorizontal: 6,
  },
  footer: {
    fontSize: 12,
    fontWeight: "bold",
    marginTop: 20,
  },
  footerLink: {
    color: "#3B82F6",
    textDecoration: "underline",
  },
  inlineContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  rupeeImage: {
    width: 8,
    height: 6,
    marginHorizontal: 2,
  },
});

// Date formatting function (if needed)
const formatDate = (dateString, fallback = 0) => {
  if (!dateString) return fallback;
  const parsedDate = new Date(dateString);
  return isNaN(parsedDate.getTime()) ? fallback : parsedDate.toLocaleDateString();
};

// Create the PDF component
const FundPerformanceTemplate = ({ selectedSchemeOptions = [], schemeFundPerformanceData, investorInfo }) => {
  const total_investment = selectedSchemeOptions.length > 0
    ? investorInfo && investorInfo?.schemes && investorInfo?.schemes
        .filter(scheme => {
          const [schemeName, schemeClass] = selectedSchemeOptions[0].split("-");
          return scheme.scheme_class === schemeClass && scheme.scheme_name === schemeName;
        })
        .map(scheme => scheme.contribution)
    : [];



  const invoices = [
    {
      returns: "Scheme Returns (%)",
      oneMonths: !schemeFundPerformanceData?.scheme_return?.one_month
        ? "N/A"
        : `${Math.round(schemeFundPerformanceData?.scheme_return?.one_month * 100)}%`,
      threeMonths: !schemeFundPerformanceData?.scheme_return?.three_month
        ? "N/A"
        : `${Math.round(schemeFundPerformanceData?.scheme_return?.three_month * 100)}%`,
      sixMonths: !schemeFundPerformanceData?.scheme_return?.six_months
        ? "N/A"
        : `${Math.round(schemeFundPerformanceData?.scheme_return?.six_months * 100)}%`,
      oneYear: !schemeFundPerformanceData?.scheme_return?.one_year
        ? "N/A"
        : `${Math.round(schemeFundPerformanceData?.scheme_return?.one_year * 100)}%`,
      sinceInception: !schemeFundPerformanceData?.scheme_return?.since_inception
        ? "N/A"
        : `${Math.round(schemeFundPerformanceData?.scheme_return?.since_inception * 100)}%`,
    },
    {
      returns: "Benchmark Returns (%)",
      oneMonths: !schemeFundPerformanceData?.benchmark_return?.one_month
        ? "N/A"
        : `${Math.round(schemeFundPerformanceData?.benchmark_return?.one_month * 100)}%`,
      threeMonths: !schemeFundPerformanceData?.benchmark_return?.three_month
        ? "N/A"
        : `${Math.round(schemeFundPerformanceData?.benchmark_return?.three_month * 100)}%`,
      sixMonths: !schemeFundPerformanceData?.benchmark_return?.six_months
        ? "N/A"
        : `${Math.round(schemeFundPerformanceData?.benchmark_return?.six_months * 100)}%`,
      oneYear: !schemeFundPerformanceData?.benchmark_return?.one_year
        ? "N/A"
        : `${Math.round(schemeFundPerformanceData?.benchmark_return?.one_year * 100)}%`,
      sinceInception: !schemeFundPerformanceData?.benchmark_return?.since_inception
        ? "N/A"
        : `${Math.round(schemeFundPerformanceData?.benchmark_return?.since_inception * 100)}%`,
    },
  ];

  const invoiceTwo = [
    {
      returns:( <View style={styles.inlineContainer}>
      <Text>Scheme (</Text>
      <Image src={rupeeSymbol} style={styles.rupeeImage} />
      <Text>)</Text>
    </View>),
      oneMonths: !schemeFundPerformanceData?.scheme_return?.one_month
        ? "N/A"
        : `${(Number((total_investment * (1 + schemeFundPerformanceData?.scheme_return?.one_month / 100)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      threeMonths: !schemeFundPerformanceData?.scheme_return?.three_month
        ? "N/A"
        : `${(Number((total_investment * (1 + schemeFundPerformanceData?.scheme_return?.three_month / 100)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      sixMonths: !schemeFundPerformanceData?.scheme_return?.six_months
        ? "N/A"
        : `${(Number((total_investment * (1 + schemeFundPerformanceData?.scheme_return?.six_months / 100)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      oneYear: !schemeFundPerformanceData?.scheme_return?.one_year
        ? "N/A"
        : `${(Number((total_investment * (1 + schemeFundPerformanceData?.scheme_return?.one_year / 100)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      sinceInception: !schemeFundPerformanceData?.scheme_return?.since_inception
        ? "N/A"
        : `${(Number((total_investment * (1 + schemeFundPerformanceData?.scheme_return?.since_inception / 100)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      returns: ( <View style={styles.inlineContainer}>
        <Text>Benchmark (</Text>
        <Image src={rupeeSymbol} style={styles.rupeeImage} />
        <Text>)</Text>
      </View>),
      oneMonths: !schemeFundPerformanceData?.benchmark_return?.one_month
        ? "N/A"
        : `${(Number((total_investment * (1 + schemeFundPerformanceData?.benchmark_return?.one_month / 100)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      threeMonths: !schemeFundPerformanceData?.benchmark_return?.three_month
        ? "N/A"
        : `${(Number((total_investment * (1 + schemeFundPerformanceData?.benchmark_return?.three_month / 100)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      sixMonths: !schemeFundPerformanceData?.benchmark_return?.six_months
        ? "N/A"
        : `${(Number((total_investment * (1 + schemeFundPerformanceData?.benchmark_return?.six_months / 100)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      oneYear: !schemeFundPerformanceData?.benchmark_return?.one_year
        ? "N/A"
        : `${(Number((total_investment * (1 + schemeFundPerformanceData?.benchmark_return?.one_year / 100)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      sinceInception: !schemeFundPerformanceData?.benchmark_return?.since_inception
        ? "N/A"
        : `${(Number((total_investment * (1 + schemeFundPerformanceData?.benchmark_return?.since_inception / 100)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
  ];

  return (
    <View>
      
        {/* Performance Section */}
        <View style={styles.section}>
          <Text style={styles.title}>Performance</Text>
        </View>

        {/* Table for Returns */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeader, { width: "20%",borderTopLeftRadius: 5 }]}></Text>
            <Text style={[styles.tableHeader, { width: "16%" }]}>1 Month</Text>
            <Text style={[styles.tableHeader, { width: "16%" }]}>3 Months</Text>
            <Text style={[styles.tableHeader, { width: "16%" }]}>6 Months</Text>
            <Text style={[styles.tableHeader, { width: "16%" }]}>1 Year</Text>
            <Text style={[styles.tableHeader, { width: "16%" ,borderTopRightRadius:5}]}>Since Inception</Text>
          </View>

          {/* Table Body */}
          {invoices.map((invoice, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: "20%",fontSize: 7 ,textAlign: "start"}]}>{invoice.returns}</Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>{invoice.oneMonths}</Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>{invoice.threeMonths}</Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>{invoice.sixMonths}</Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>{invoice.oneYear}</Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>{invoice.sinceInception}</Text>
            </View>
          ))}
        </View>

        {/* Investment Value Section */}
        <View style={styles.section}>
          <Text style={{ textAlign: "center", fontSize: 8, fontWeight: "bold", marginBottom: 10 ,fontFamily: "Open Sans",}}>
            Value of Investment of  <Image src={rupeeSymbol} style={styles.rupeeImage} />  {Number(total_investment).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        </View>

        {/* Table for Investment Value */}
        <View style={styles.table}>
          {invoiceTwo.map((invoice, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: "20%",fontSize: 7,textAlign: "start" }]}>{invoice.returns}</Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>{invoice.oneMonths}</Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>{invoice.threeMonths}</Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>{invoice.sixMonths}</Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>{invoice.oneYear}</Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>{invoice.sinceInception}</Text>
            </View>
          ))}
        </View>

        {/* Disclaimer */}
        <Text style={styles.disclaimer}>
          *Past performance may or may not be sustained in future and is not a guarantee of any future returns.
        </Text>

      
     
    </View>
  );
};

export default FundPerformanceTemplate;